// Utilidades compartidas por las páginas del panel.

const DAY = 24 * 60 * 60 * 1000;

export const daysAgo = (n) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return d.getTime();
};

export const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
};

/** Clave de día en hora LOCAL: toISOString() la daba en UTC y movía las ventas de la noche al día siguiente. */
export const dayKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const lastDays = (n) =>
  [...Array(n)].map((_, i) => dayKey(Date.now() - (n - 1 - i) * DAY));

export function formatMoney(value, currency = 'USD') {
  try {
    return new Intl.NumberFormat('es', { style: 'currency', currency: currency || 'USD' }).format(value || 0);
  } catch {
    // Código de moneda que Intl no reconoce: se muestra tal cual.
    return `${(value || 0).toFixed(2)} ${currency}`;
  }
}

/** Suma por moneda, p. ej. «$ 120,00 · Bs. 3.400,00». Las tiendas no comparten moneda. */
export function formatTotals(totals) {
  const entries = Object.entries(totals).filter(([, v]) => v !== 0);
  if (entries.length === 0) return formatMoney(0);
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([currency, value]) => formatMoney(value, currency))
    .join(' · ');
}

export const addTo = (totals, currency, value) => {
  const key = currency || 'USD';
  totals[key] = (totals[key] || 0) + (value || 0);
  return totals;
};

export function formatDate(ms, withTime = true) {
  if (!ms) return '—';
  const date = typeof ms?.toMillis === 'function' ? new Date(ms.toMillis()) : new Date(ms);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es', withTime
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' });
}

export const formatQty = (n) =>
  new Intl.NumberFormat('es', { maximumFractionDigits: 2 }).format(n || 0);

/**
 * `price` es el precio del lote; lo que se cobra es el precio por unidad.
 * Misma cuenta que Product.unitPrice en la app y perUnit() en las funciones.
 */
export function unitPrice(product = {}) {
  const pack = Number(product.packageQuantity) > 0 ? Number(product.packageQuantity) : 1;
  return (Number(product.price) || 0) / pack;
}

export const companyName = (c) => c?.name || c?.companyName || c?.companyCode || c?.id || 'Sin nombre';

export const roleLabel = (role) => (role === 'admin' ? 'Administrador' : 'Empleado');

/** Descarga un CSV que Excel abre con acentos (de ahí el BOM). */
export function downloadCsv(filename, headers, rows) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Mensaje legible para un error de Firestore o de Auth. */
export function errorMessage(error) {
  switch (error?.code) {
    case 'permission-denied':
      return 'Sin permiso: las reglas de Firestore rechazaron la consulta.';
    case 'unavailable':
      return 'No hay conexión con Firebase.';
    case 'failed-precondition':
      return 'Falta un índice de Firestore para esta consulta.';
    default:
      return error?.message || 'Error desconocido.';
  }
}
